'use client';

import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import s from './MediaMonitorFilter.module.scss';
import Popover from '../Popover/Popover';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import { t } from '../../i18n';
import Icon from '../Icon';
import Checkbox from '../Checkbox/Checkbox';

const MediaMonitorFilter = ({
    filters = {},
    searchUrl,
    onQueryLoadingChange,
}) => {
    const popoverRef = useRef(null);
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [hadInitialFilters, setHadInitialFilters] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const query = params.get('q');
        if (query) {
            setSearchValue(query);
        }

        const urlTopics = params.get('topics');
        const urlCountries = params.get('countries');

        if (urlTopics) {
            setSelectedTopics(urlTopics.split(','));
        }

        if (urlCountries) {
            setSelectedCountries(urlCountries.split(','));
        }

        // Track if any filters were applied initially (blank slate vs existing filters)
        const hadAny = Boolean(
            (urlTopics && urlTopics.length) ||
            (urlCountries && urlCountries.length)
        );
        setHadInitialFilters(hadAny);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                if (isFilterDropdownOpen) {
                    setIsFilterDropdownOpen(false);
                    handleSearchClick();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterDropdownOpen, selectedTopics, selectedCountries]);

    const toggleFilterDropdown = () => {
        setIsFilterDropdownOpen(!isFilterDropdownOpen);
    };

    const handleSearchChange = (value) => {
        setSearchValue(value);
    };

    const handleSearchClick = () => {
        if (searchValue || selectedTopics.length || selectedCountries.length) {
            onQueryLoadingChange(true);
            const params = new URLSearchParams();

            if (searchValue) {
                params.append('q', searchValue);
            }

            if (selectedTopics.length > 0) {
                params.append('topics', selectedTopics.join(','));
            }

            if (selectedCountries.length > 0) {
                params.append('countries', selectedCountries.join(','));
            }

            const url = `${searchUrl}?${params.toString()}`;

            window.location.href = url;
        } else if (!searchValue.trim()) {
            handleGoBackToMediaMonitor();
        }
    };

    const handleSearchClear = () => {
        if (!searchValue) return;

        setSearchValue('');

        const isOnSearchPage = window.location.pathname.includes('/search/');
        if (!isOnSearchPage) {
            onQueryLoadingChange?.(false);
            return;
        }

        onQueryLoadingChange(true);
        window.location.href = searchUrl.replace('/search/', '/');
    };

    const handleGoBackToMediaMonitor = () => {
        onQueryLoadingChange(true);

        const params = new URLSearchParams();
        let shouldGoBackToMMStartPage = true;

        if (selectedTopics.length > 0) {
            params.append('topics', selectedTopics.join(','));
            shouldGoBackToMMStartPage = false;
        }

        if (selectedCountries.length > 0) {
            params.append('countries', selectedCountries.join(','));
            shouldGoBackToMMStartPage = false;
        }

        let url = `${searchUrl}?${params.toString()}`;

        if (shouldGoBackToMMStartPage) {
            url = `${searchUrl.replace('/search/', '/')}`;
        }

        window.location.href = url;
    };

    const handleTopicChange = (topicId) => {
        setSelectedTopics((prevSelectedTopics) => {
            if (prevSelectedTopics.includes(topicId)) {
                return prevSelectedTopics.filter((id) => id !== topicId);
            } else {
                return [...prevSelectedTopics, topicId];
            }
        });
    };

    const handleCountryChange = (countryValue) => {
        setSelectedCountries((prevSelectedCountries) => {
            if (prevSelectedCountries.includes(countryValue)) {
                return prevSelectedCountries.filter(
                    (country) => country !== countryValue
                );
            } else {
                return [...prevSelectedCountries, countryValue];
            }
        });
    };

    const onClearAllFilters = () => {
        if (!selectedTopics.length && !selectedCountries.length) {
            return;
        }

        // Immediately clear local state to provide instant UI feedback
        setSelectedTopics([]);
        setSelectedCountries([]);

        onQueryLoadingChange(true);
        const params = new URLSearchParams();

        let url = '';

        if (searchValue) {
            params.append('q', searchValue);
            url = `${searchUrl}?${params.toString()}`;
        } else {
            // go to start page if no search value
            url = `${searchUrl.replace('/search/', '/')}`;
        }

        window.location.href = url;
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                if (!searchValue.trim()) {
                    handleGoBackToMediaMonitor();
                } else {
                    handleSearchClick();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [searchValue]);

    const activeFilters = useMemo(() => {
        return selectedTopics.length + selectedCountries.length;
    }, [selectedTopics, selectedCountries]);

    return (
        <div className={s.Root}>
            <div className={s.Container}>
                <FilterDropdown
                    popoverRef={popoverRef}
                    isFilterDropdownOpen={isFilterDropdownOpen}
                    toggleFilterDropdown={toggleFilterDropdown}
                    hadInitialFilters={hadInitialFilters}
                    topics={filters.topics}
                    selectedTopics={selectedTopics}
                    handleTopicChange={handleTopicChange}
                    countries={filters.countries}
                    selectedCountries={selectedCountries}
                    handleCountryChange={handleCountryChange}
                    onClearAllFilters={onClearAllFilters}
                    handleSearchClick={handleSearchClick}
                    activeFilters={activeFilters}
                />
                <div className={s.SearchWrapper}>
                    <div className={s.SearchField}>
                        <Icon name="Search" className={s.SearchIcon} />
                        <input
                            className={s.SearchInput}
                            type="text"
                            placeholder={t('mediaMonitorFilter.searchKeywords')}
                            value={searchValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        {searchValue && (
                            <button
                                className={s.ClearButton}
                                onClick={handleSearchClear}>
                                <Icon
                                    name="CloseSmall"
                                    className={s.ClearIcon}
                                />
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleSearchClick}
                        className={s.SearchButton}>
                        {t('mediaMonitorFilter.search')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FilterDropdown = React.memo(
    ({
        popoverRef,
        isFilterDropdownOpen,
        toggleFilterDropdown,
        hadInitialFilters = false,
        topics = [],
        selectedTopics,
        handleTopicChange,
        countries = [],
        selectedCountries,
        handleCountryChange,
        onClearAllFilters,
        handleSearchClick,
        activeFilters,
    }) => {
        const [searchTopic, setSearchTopic] = useState('');
        const [searchCountry, setSearchCountry] = useState('');

        const isFilterApplied = useMemo(() => {
            return selectedTopics.length > 0 || selectedCountries.length > 0;
        }, [selectedTopics, selectedCountries]);

        // Show "Done" when filters are active OR when the page initially had filters
        // (user cleared them and should be able to apply the cleared state).
        const showDoneButton = isFilterApplied || hadInitialFilters;

        const renderSearchInput = (value, onChange, onClear) => (
            <div className={s.DropdownSearchWrapper}>
                <Icon
                    name="Search"
                    className={s.DropdownSearchIcon}
                    size="Small"
                />
                <input
                    type="text"
                    placeholder="Search..."
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={s.DropdownSearch}
                />
                {value.length > 0 && (
                    <button className={s.DropdownClear} onClick={onClear}>
                        <Icon
                            name="Close"
                            className={s.DropdownClearIcon}
                            size="Small"
                        />
                    </button>
                )}
            </div>
        );

        return (
            <div className={s.FilterDropdown}>
                <Popover
                    minWidth="233px"
                    popoverRef={popoverRef}
                    modifiers={['Filter']}
                    isPopoverOpen={isFilterDropdownOpen}
                    onClick={toggleFilterDropdown}
                    content={
                        <div className={s.PopoverContent}>
                            {countries && countries.length > 1 && (
                                <Dropdown
                                    modifiers={['BorderBottom']}
                                    title={t('mediaMonitorFilter.countries')}
                                    showCheckmark={
                                        selectedCountries.length > 0
                                    }>
                                    {renderSearchInput(
                                        searchCountry,
                                        setSearchCountry,
                                        () => setSearchCountry('')
                                    )}
                                    <ul className={s.CheckboxListScroll}>
                                        {countries
                                            .filter((country) =>
                                                country.label
                                                    .toLowerCase()
                                                    .includes(
                                                        searchCountry.toLowerCase()
                                                    )
                                            )
                                            .map((country) => (
                                                <li
                                                    key={country.value}
                                                    className={s.CheckboxItem}>
                                                    <span
                                                        className={
                                                            s.CheckboxLabel
                                                        }>
                                                        {country.label}
                                                    </span>
                                                    <Checkbox
                                                        modifier="Alternative"
                                                        value={country.value}
                                                        checked={selectedCountries.includes(
                                                            country.value
                                                        )}
                                                        onChange={() =>
                                                            handleCountryChange(
                                                                country.value
                                                            )
                                                        }
                                                    />
                                                </li>
                                            ))}
                                    </ul>
                                </Dropdown>
                            )}

                            {topics && topics.length > 0 && (
                                <Dropdown
                                    modifiers={['BorderBottom']}
                                    title={t('mediaMonitorFilter.topics')}
                                    showCheckmark={selectedTopics.length > 0}>
                                    {renderSearchInput(
                                        searchTopic,
                                        setSearchTopic,
                                        () => setSearchTopic('')
                                    )}
                                    <ul className={s.CheckboxListScroll}>
                                        {topics
                                            .filter((topic) =>
                                                topic.label
                                                    .toLowerCase()
                                                    .includes(
                                                        searchTopic.toLowerCase()
                                                    )
                                            )
                                            .map((topic) => (
                                                <li
                                                    key={topic.id}
                                                    className={s.CheckboxItem}>
                                                    <span
                                                        className={
                                                            s.CheckboxLabel
                                                        }>
                                                        {topic.label
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                            topic.label.slice(
                                                                1
                                                            )}
                                                    </span>
                                                    <Checkbox
                                                        modifier="Alternative"
                                                        value={topic.id}
                                                        checked={selectedTopics.includes(
                                                            topic.id
                                                        )}
                                                        onChange={() =>
                                                            handleTopicChange(
                                                                topic.id
                                                            )
                                                        }
                                                    />
                                                </li>
                                            ))}
                                    </ul>
                                </Dropdown>
                            )}

                            <div className={s.FilterButtons}>
                                {isFilterApplied && (
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={onClearAllFilters}>
                                        {t('mapFilter.clear')}
                                        <Icon
                                            name="Close"
                                            className={s.FilterIcon}
                                        />
                                    </button>
                                )}
                                {isFilterApplied && (
                                    <div className={s.FilterSeparator} />
                                )}
                                {showDoneButton && (
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={handleSearchClick}>
                                        {t('mapFilter.done')}
                                    </button>
                                )}
                            </div>
                        </div>
                    }
                    overrideButton={
                        <Button
                            title={
                                isFilterDropdownOpen
                                    ? t('mapFilter.close')
                                    : t('mapFilter.filter')
                            }
                            modifier="Filter"
                            className={s.FilterButton}
                            size="Small"
                            icon="FilterWhite"
                            isActive={isFilterDropdownOpen}
                            isFullWidth
                            description={
                                activeFilters > 0 && `(${activeFilters})`
                            }
                        />
                    }
                />
            </div>
        );
    }
);

MediaMonitorFilter.propTypes = {
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    onSearchClick: PropTypes.func,
};

export default MediaMonitorFilter;
